import { MarkerClusterer, DefaultRenderer } from '@googlemaps/markerclusterer';
import { MarkerFactory } from './MarkerUtils';

export function LocationsMap(options) {

    this.map = null;
    this.mapContainer = document.getElementById('c-locations__google-map');
    this.mapDefaults = {
        zoom: 2,
        center: {lat: 22, lng: 4}
    }
    this.locations = null
    this.options = options;
    this.mapDefaultStyles =
        [
            {
                'featureType': 'all',
                'elementType': 'all',
                'stylers': [
                    {'saturation': -96}
                ]
            },
            {
                'featureType': 'water',
                'stylers': [
                    {'saturation': -100},
                    {'lightness': 73}
                ]
            },
            {
                'elementType': 'geometry',
                'stylers': [
                    {'gamma': 2.78}
                ]
            },
            {
                'elementType': 'labels',
                'stylers': [
                    {'lightness': 27}
                ]
            },
            {
                'featureType': 'road',
                'elementType': 'labels',
                'stylers': [
                    {'visibility': 'off'}
                ]
            },
            {
                'featureType': 'water',
                'stylers': [
                    {'color': '#dddddd'},
                    {'visibility': 'on'}
                ]
            },
            {
                'featureType': 'landscape',
                'elementType': 'geometry',
                'stylers': [
                    {'lightness': 47}
                ]
            },
            {
                'featureType': 'administrative.province',
                'stylers': [
                    {'visibility': 'off'}
                ]
            }
        ];
    this.mapCountryStyles = this.mapDefaultStyles.concat([{
        'featureType': 'administrative.country',
        'stylers': [
            {'gamma': 0.53},
            {'hue': '#ff8000'},
            {'saturation': 58},
            {'visibility': 'off'}
        ]
    }]);
    this.mapCountryZoomedStyles = this.mapDefaultStyles.concat([{
        'featureType': 'administrative.country',
        'stylers': [
            {'gamma': 0.53},
            {'hue': '#ff8000'},
            {'saturation': 58},
            {'visibility': 'on'}
        ]
    }]);
    this.featureStyles = {
        countryStyle: {
            strokeColor: '#ffffff',
            strokeWeight: 1,
            strokeOpacity: 1,
            fillOpacity: 1.0,
            fillColor: '#cccccc',
            clickable: true
        },
        countryNonactiveStyle: {
            strokeColor: '#ffffff',
            strokeWeight: 1,
            strokeOpacity: 1,
            fillOpacity: 1.0,
            fillColor: '#f0f0f0',
            clickable: false
        },
        countryHoverStyle: {
            strokeColor: '#ffffff',
            strokeWeight: 1,
            strokeOpacity: 1,
            fillOpacity: 1.0,
            fillColor: '#aaaaaa',
            clickable: true
        },
        countrySelectStyle: {
            strokeColor: '#ffffff',
            strokeWeight: 1,
            strokeOpacity: 0.05,
            fillOpacity: 0.15,
            fillColor: '#aaaaaa',
            clickable: true
        },
        countryZoomedStyle: {
            strokeColor: '#ea6f18',
            strokeWeight: 1,
            strokeOpacity: 0,
            fillOpacity: 0.0,
            fillColor: '#cccccc',
            clickable: true
        },
        countryZoomedHoverStyle: {
            strokeColor: '#cccccc',
            strokeWeight: 0,
            strokeOpacity: 0,
            fillOpacity: 0.3,
            fillColor: '#aaaaaa',
            clickable: true
        },
        regionStyle: {
            strokeColor: '#ea6f18',
            strokeWeight: 2,
            strokeOpacity: 1,
            fillOpacity: 0.3,
            fillColor: '#aaaaaa',
            zIndex: 999,
            clickable: true
        },
        regionHoverStyle: {
            strokeColor: '#ea6f18',
            strokeWeight: 1,
            strokeOpacity: 1,
            fillOpacity: 0.4,
            fillColor: '#aaaaaa',
            zIndex: 999,
            clickable: true
        },
        regionSelectStyle: {
            strokeColor: '#ea6f18',
            strokeWeight: 2,
            strokeOpacity: 1,
            fillOpacity: 0.2,
            fillColor: '#ea6f18',
            zIndex: 999,
            clickable: true
        }
    };
    this.selectedFeature = null;
    this.regionMarker = null;
    this.countryLabelId = 'c-locations__maps-country-label';
    this.consultantMarkers = [];
    this.isMapInCountryView = true;
    // callback functions
    this.showCountryList = null;
    this.clearCountryList = null;
    this.setCountrySelectbox = null;

    this.openMap = function () {
        // Check if Advanced Markers are enabled and get Map ID
        // Store as instance properties so event handlers can access them
        this.useAdvancedMarkers = this.mapContainer.dataset.useAdvancedMarkers === '1'
            || this.mapContainer.dataset.useAdvancedMarkers === 'true';
        this.mapId = this.mapContainer.dataset.mapId || '';

        // Map configuration
        const mapConfig = {
            zoom: this.mapDefaults.zoom,
            maxZoom: 17,
            streetViewControl: false,
            fullscreenControl: false,
            mapTypeControl: false,
            zoomControlOptions: {
                position: google.maps.ControlPosition.TOP_LEFT
            },
            center: this.mapDefaults.center,
        };

        // Add mapId if Advanced Markers are enabled
        if (this.useAdvancedMarkers && this.mapId) {
            mapConfig.mapId = this.mapId;
            // Note: When using mapId, styles must be configured in Google Cloud Console
            // Inline styles are not allowed with cloud-based map styling
        } else {
            // Only add inline styles when NOT using a Map ID
            mapConfig.styles = this.mapCountryStyles;
        }

        this.map = new google.maps.Map(this.mapContainer, mapConfig);

        this.map.data.addGeoJson(this.options.countryFeature);

        this.initMapEvents();
        this.setConsultantMarker();
        this.setConsultantMarkerClusterer();
        this.updateFeatureStyles();
        this.setInitialBounds();
    }

    this.initMapEvents = function () {
        this.map.data.addListener('click', (event) => {
            if (this.selectedFeature === null || (this.selectedFeature.getProperty('uid') !== event.feature.getProperty('uid'))) {
                this.selectFeature(event.feature);
            } else if (this.selectedFeature !== null && event.feature.getProperty('type') === 'country') {
                this.selectFeature(this.selectedFeature);
            }
        });

        this.map.data.addListener('mouseover', (event) => {
            this.updateFeatureHoverStyle(event.feature, 'mouseover');
        });

        this.map.data.addListener('mouseout', (event) => {
            this.updateFeatureHoverStyle(event.feature, 'mouseout');
        });

        this.map.addListener('zoom_changed', () => {
            let zoomLevel = this.map.getZoom();

            // Only change styles dynamically when NOT using a Map ID
            // (Map ID uses cloud-based styling that cannot be changed at runtime)
            if (!this.useAdvancedMarkers || !this.mapId) {
                if (zoomLevel > 2 && this.isMapInCountryView === true) {
                    this.isMapInCountryView = false;
                    this.map.setOptions({
                        styles: this.mapCountryZoomedStyles
                    });
                } else if (zoomLevel <= 2 && this.isMapInCountryView === false) {
                    this.isMapInCountryView = true;
                    this.map.setOptions({
                        styles: this.mapCountryStyles
                    });
                    this.removeAllRegions();
                }
            } else {
                // Track country view state even when styles cannot be changed
                if (zoomLevel > 2 && this.isMapInCountryView === true) {
                    this.isMapInCountryView = false;
                } else if (zoomLevel <= 2 && this.isMapInCountryView === false) {
                    this.isMapInCountryView = true;
                    this.removeAllRegions();
                }
            }

            this.updateFeatureStyles();
            this.updateFeatureSelectedStyle();
            this.updateConsultantMarkers();
        });
    }

    this.processBoundsPoints = function (geometry, callback, thisArg) {
        let that = this;

        if (geometry instanceof google.maps.LatLng) {
            callback.call(thisArg, geometry);
        } else if (geometry instanceof google.maps.Data.Point) {
            callback.call(thisArg, geometry.get());
        } else {
            geometry.getArray().forEach(function (g) {
                that.processBoundsPoints(g, callback, thisArg);
            });
        }
    }

    this.updateFeatureStyles = function () {
        this.map.data.revertStyle();

        this.map.data.forEach(feature => {
            let style;

            if (this.isMapInCountryView) {
                if (feature.getProperty('is_active') !== true) {
                    style = this.featureStyles.countryNonactiveStyle;
                } else {
                    style = this.featureStyles.countryStyle;
                }
            } else {
                if (feature.getProperty('type') === 'region') {
                    style = this.featureStyles.regionStyle;
                } else {
                    // need to clone styles here otherwise clickable is overwritten in global object
                    style = {...this.featureStyles.countryZoomedStyle};
                    style.clickable = feature.getProperty('is_active');
                }
            }

            this.map.data.overrideStyle(feature, style);
        });
    }

    this.updateFeatureHoverStyle = function (feature, eventName) {
        let style = this.featureStyles.countryStyle;

        if (eventName === 'mouseover') {
            if (feature.getProperty('type') === 'country') {
                if (this.isMapInCountryView) {
                    style = this.featureStyles.countryHoverStyle;
                } else {
                    style = this.featureStyles.countryZoomedHoverStyle;
                    if (this.map.getZoom() > 9) {
                        style = this.featureStyles.countryZoomedStyle;
                    }
                }
                this.addCountryLabel(feature);
            } else {
                if (this.selectedFeature && this.selectedFeature.getProperty('uid') === feature.getProperty('uid')) {
                    style = this.featureStyles.regionSelectStyle;
                } else {
                    style = this.featureStyles.regionHoverStyle;
                    if (this.map.getZoom() > 9) {
                        style = this.featureStyles.regionStyle;
                    }
                    this.setRegionMarker(feature);
                }
            }
        } else if (eventName === 'mouseout') {
            if (feature.getProperty('type') === 'country') {
                if (this.isMapInCountryView) {
                    style = this.featureStyles.countryStyle;
                } else {
                    style = this.featureStyles.countryZoomedStyle;
                }
                this.removeCountryLabel();
            } else {
                style = this.featureStyles.regionStyle;
                this.removeRegionMarker();
            }
        }

        this.map.data.overrideStyle(feature, style);

        // reset selected style
        if (eventName === 'mouseout') {
            this.updateFeatureSelectedStyle();
        }
    }

    this.updateFeatureSelectedStyle = function () {
        if (this.selectedFeature && this.isMapInCountryView === false) {
            let style;

            if (this.selectedFeature.getProperty('type') === 'country') {
                style = this.featureStyles.countrySelectStyle;
                this.addCountryLabel(this.selectedFeature);
            } else {
                style = this.featureStyles.regionSelectStyle;
            }

            this.map.data.overrideStyle(this.selectedFeature, style);
        }
    }

    this.revertFeatureSelectedStyle = function () {
        if (this.selectedFeature) {
            let type = this.selectedFeature.getProperty('type');

            if (type === 'region') {
                this.map.data.overrideStyle(this.selectedFeature, this.featureStyles.regionStyle);
            } else {
                this.map.data.overrideStyle(this.selectedFeature, this.featureStyles.countryZoomedStyle);
            }
        }
    }

    this.addRegionsToMap = function (clickedFeature, isoA2) {
        if (this.selectedFeature.getProperty('type') === 'country') {
            this.removeAllRegions();

            if (this.options.regionsFeatures[isoA2]) {
                this.map.data.overrideStyle(clickedFeature, {visible: false});
                this.map.data.addGeoJson(this.options.regionsFeatures[isoA2]);

                this.updateFeatureStyles();
            }
        }
    }

    this.removeAllRegions = function () {
        this.map.data.forEach(feature => {
            if (feature.getProperty('type') === 'region') {
                this.map.data.remove(feature);
            }
        });
    }

    this.fitIndividualBoundsForCountry = function (isoA2) {
        let bounds;
        let offset = 0;

        switch (isoA2) {
            case 'FR':
                bounds = new google.maps.LatLngBounds(new google.maps.LatLng(41, -8), new google.maps.LatLng(52, 14));
                offset = -200;
                break;
            case 'NL':
                bounds = new google.maps.LatLngBounds(new google.maps.LatLng(50.28, -0.09), new google.maps.LatLng(53.79, 11.14));
                offset = -200;
                break;
            case 'DE':
            case 'RU':
            case 'US':
            case 'CA':
                offset = -200;
                break;

            default:
                break;
        }

        if (!bounds) {
            bounds = this.fitBoundsForSelectedFeature();
        }

        this.map.fitBounds(bounds, offset);
    }

    this.fitBoundsForSelectedFeature = function () {
        let bounds = new google.maps.LatLngBounds();
        this.processBoundsPoints(this.selectedFeature.getGeometry(), bounds.extend, bounds);

        return bounds;
    }

    this.setInitialBounds = function () {
        if (this.options.countryCount > 0 && this.options.countryCount < 50) {
            if (this.options.countryCount === 1) {
                this.selectCountryByIsoCode(this.options.countryCodes[0]);
            } else {
                let bounds = new google.maps.LatLngBounds();

                this.map.data.forEach(feature => {
                    let geometry = feature.getGeometry();
                    if (geometry !== null && feature.getProperty('is_active')) {
                        this.processBoundsPoints(geometry, bounds.extend, bounds);
                    }
                });

                this.map.fitBounds(bounds);
            }
        }
    }

    this.setRegionMarker = function (feature) {
        if (feature.getProperty('type') === 'region') {
            // Support BOTH old and new property names (backward compatibility):
            const customLng = feature.getProperty('marker_longitude') || feature.getProperty('marker_lng');
            const customLat = feature.getProperty('marker_latitude') || feature.getProperty('marker_lat');

            // Determine position (both modes use same logic):
            let markerPosition;
            // Parse coordinates and validate (handles edge cases like 0,0 correctly)
            const lat = parseFloat(customLat);
            const lng = parseFloat(customLng);

            if (!isNaN(lat) && !isNaN(lng) && (lat !== 0 || lng !== 0)) {
                // Use custom coordinates if valid and not 0,0 (which indicates no custom position)
                markerPosition = new google.maps.LatLng(lat, lng);
            } else {
                // Fall back to calculated center from geometry bounds
                let bounds = new google.maps.LatLngBounds();
                this.processBoundsPoints(feature.getGeometry(), bounds.extend, bounds);
                markerPosition = bounds.getCenter();
            }

            let map = this.map;
            // Get feature flag from data attribute
            const USE_ADVANCED_MARKERS = this.mapContainer.dataset.useAdvancedMarkers === '1'
                || this.mapContainer.dataset.useAdvancedMarkers === 'true';

            // Create marker based on feature flag:
            if (USE_ADVANCED_MARKERS) {
                // NEW: AdvancedMarkerElement
                this.regionMarker = MarkerFactory.createRegionMarker(
                    markerPosition,
                    (feature.getProperty('name') || '').toString(),
                    map
                );
            } else {
                // LEGACY: google.maps.Marker (keep existing code)
                this.regionMarker = new google.maps.Marker({
                    position: markerPosition,
                    clickable: false,
                    label: {
                        text: feature.getProperty('name').toString(),
                        color: '#747474',
                        fontWeight: '900'
                    },
                    map: map,
                    zIndex: google.maps.Marker.MAX_ZINDEX,
                    icon: {
                        path: google.maps.SymbolPath.CIRCLE,
                        fillColor: "#ffffff",
                        fillOpacity: 1,
                        strokeWeight: 1,
                        strokeColor: "#ffffff",
                        scale: 13,
                    },
                });
            }
        }
    }

    this.removeRegionMarker = function () {
        this.regionMarker.setMap(null);
    }

    this.addCountryLabel = function (feature) {
        if (feature.getProperty('type') === 'country') {
            let isoA2 = feature.getProperty('iso_a2');
            let countryName = this.options.countryNames[isoA2];

            if (countryName !== '') {
                let labelContainer = document.getElementById(this.countryLabelId);
                if (labelContainer === null) {
                    labelContainer = document.createElement('div');
                    labelContainer.id = this.countryLabelId;
                }
                labelContainer.textContent = this.options.countryNames[isoA2];

                this.mapContainer.appendChild(labelContainer);
            }
        }
    }

    this.removeCountryLabel = function () {
        let labelContainer = document.getElementById(this.countryLabelId);
        if (labelContainer !== null) {
            labelContainer.remove();
        }
    }

    this.setConsultantMarker = function () {
        let markerArray = JSON.parse(this.mapContainer.dataset.marker);
        let map = this.map;

        // Get feature flag from data attribute
        const USE_ADVANCED_MARKERS = this.mapContainer.dataset.useAdvancedMarkers === '1'
            || this.mapContainer.dataset.useAdvancedMarkers === 'true';

        // KEEP OverlappingMarkerSpiderfier for legacy mode
        let oms = null;
        if (!USE_ADVANCED_MARKERS) {
            oms = new OverlappingMarkerSpiderfier(map, {
                markersWontMove: true,
                markersWontHide: true,
                basicFormatEvents: true
            });
        }

        if (markerArray !== null && markerArray.length > 0) {
            markerArray.forEach(markerObj => {
                let consultantMarker;
                let consultantInfo = new google.maps.InfoWindow({
                    content: markerObj.label
                });

                if (USE_ADVANCED_MARKERS) {
                    // NEW: AdvancedMarkerElement
                    consultantMarker = MarkerFactory.createConsultantMarker(
                        {lat: markerObj.coord.lat, lng: markerObj.coord.lng},
                        markerObj.color,
                        map
                    );

                    // Event listeners for AdvancedMarkerElement:
                    consultantMarker.content.addEventListener('mouseenter', () => {
                        consultantInfo.open({ anchor: consultantMarker, map });
                    });

                    consultantMarker.content.addEventListener('mouseleave', () => {
                        consultantInfo.close();
                    });

                    consultantMarker.addListener('gmp-click', () => {
                        this.loadLocation(markerObj.id);
                    });
                } else {
                    // LEGACY: google.maps.Marker
                    consultantMarker = new google.maps.Marker({
                        position: {lat: markerObj.coord.lat, lng: markerObj.coord.lng},
                        clickable: true,
                        icon: {
                            path: google.maps.SymbolPath.CIRCLE,
                            fillColor: markerObj.color,
                            fillOpacity: 1,
                            strokeWeight: 0,
                            scale: 3,
                        },
                    });

                    // Legacy event listeners:
                    consultantMarker.addListener('mouseover', () => {
                        consultantInfo.open({ anchor: consultantMarker, map });
                    });

                    consultantMarker.addListener('mouseout', () => {
                        consultantInfo.close();
                    });

                    consultantMarker.addListener('spider_click', () => {
                        this.loadLocation(markerObj.id);
                    });

                    // Add to spiderfier in legacy mode:
                    oms.addMarker(consultantMarker);
                }

                this.consultantMarkers.push(consultantMarker);
            });
        }
    }

    this.setConsultantMarkerClusterer = function () {
        let map = this.map;
        let markersToCluster = this.consultantMarkers;

        // Get feature flag from data attribute
        const USE_ADVANCED_MARKERS = this.mapContainer.dataset.useAdvancedMarkers === '1'
            || this.mapContainer.dataset.useAdvancedMarkers === 'true';

        // MarkerClusterer.render = ;
        window.oink = MarkerClusterer;
        let customRenderer = new DefaultRenderer();

        customRenderer.render = function ({count, position}, stats) {
            if (USE_ADVANCED_MARKERS) {
                // NEW: HTML-based AdvancedMarkerElement
                const content = MarkerFactory.createClusterMarkerContent(count);

                return new google.maps.marker.AdvancedMarkerElement({
                    position,
                    content,
                    gmpClickable: true,
                    zIndex: count,
                });
            } else {
                // LEGACY: SVG-based Marker (keep existing code)
                const color = "#ea6f18";
                const svg = window.btoa(`
                    <svg fill="${color}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240">
                        <circle cx="120" cy="120" opacity="1" r="70" />
                        <circle cx="120" cy="120" opacity=".6" r="90" />
                        <circle cx="120" cy="120" opacity=".3" r="110" />
                    </svg>`);

                return new google.maps.Marker({
                    position,
                    icon: {
                        url: `data:image/svg+xml;base64,${svg}`,
                        scaledSize: new google.maps.Size(45, 45),
                    },
                    label: {
                        text: String(count),
                        color: "rgba(255,255,255,1)",
                        fontSize: "12px",
                    },
                    title: `Cluster of ${count} markers`,
                    zIndex: count,
                });
            }
        };

        new MarkerClusterer({ markers: markersToCluster, map: map, renderer: customRenderer });
    }

    this.updateConsultantMarkers = function () {
        // Get feature flag from data attribute
        const USE_ADVANCED_MARKERS = this.mapContainer.dataset.useAdvancedMarkers === '1'
            || this.mapContainer.dataset.useAdvancedMarkers === 'true';

        this.consultantMarkers.forEach((marker, index) => {
            if (USE_ADVANCED_MARKERS) {
                // NEW: Scale via CSS transform (matching legacy formula)
                // Note: CSS transform scale may render differently than icon.scale
                // Start with same formula, adjust if needed after testing
                let baseScale;
                if (this.map.getZoom() <= this.mapDefaults.zoom) {
                    baseScale = 3;  // Matches legacy default
                } else {
                    baseScale = 2 * (0.35 * this.map.getZoom());  // Matches legacy formula
                }

                // Apply via CSS transform (might need adjustment factor)
                MarkerFactory.scaleMarker(marker, baseScale / 3);  // Divide by 3 to normalize for HTML elements
            } else {
                // LEGACY: Scale via icon.scale property (keep existing code)
                let markerIcon = marker.getIcon();
                if (this.map.getZoom() <= this.mapDefaults.zoom) {
                    markerIcon.scale = 3;
                } else {
                    markerIcon.scale = 2 * (0.35 * this.map.getZoom());
                }
                this.consultantMarkers[index].setIcon(markerIcon);
            }
        });
    }

    this.selectFeature = function (feature) {
        this.revertFeatureSelectedStyle();

        this.selectedFeature = feature;
        let type = this.selectedFeature.getProperty('type');
        let isoA2 = this.selectedFeature.getProperty('iso_a2');

        if (type === 'country') {
            this.fitIndividualBoundsForCountry(isoA2);
            this.addRegionsToMap(this.selectedFeature, isoA2);

            this.loadCountry(isoA2);
        } else if (type === 'region') {
            this.loadRegion(isoA2, this.selectedFeature.getProperty('region'))
        }

        this.updateFeatureSelectedStyle();
    }

    this.selectCountryByIsoCode = function (isoA2) {
        if (isoA2 !== '0') {
            let feature = this.map.data.getFeatureById(isoA2);
            if (feature && feature.getGeometry() !== null) {
                this.selectFeature(feature);
            } else {
                // load country event if it's not on the map
                this.loadCountry(isoA2);
                this.resetMap();
            }
        }
    }

    this.loadLocation = function (locationId) {
        if (this.showCountryList) {
            this.showCountryList(null, locationId);
        }
    }

    this.loadCountry = function (isoA2) {
        if (this.options.regionsFeatures[isoA2]) {
            if (this.clearCountryList) {
                this.clearCountryList();
            }

            let regionHintElement = document.getElementById('map-address-no-list-anchor');
            regionHintElement.style.display = 'block';
        } else if (this.showCountryList) {
            this.showCountryList(isoA2);
        }
        if (this.setCountrySelectbox) {
            this.setCountrySelectbox(isoA2)
        }
    }

    this.loadRegion = function (isoA2, regionId) {
        if (this.showCountryList) {
            this.showCountryList(isoA2, null, regionId);
        }
    }

    this.resetMap = function () {
        this.map.setZoom(this.mapDefaults.zoom);
        this.map.setCenter(this.mapDefaults.center);
        this.selectedFeature = null;

        if (this.clearCountryList) {
            this.clearCountryList();
        }
    }

    this.showGeocodeInfoWindow = function (text, position) {
        let geocodingInfoWindow = new google.maps.InfoWindow({
            position: position,
            content: text,
            maxWidth: 200
        });

        geocodingInfoWindow.open({
            map: this.map
        });

        this.map.setCenter(position);
    }

}
